import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { setFilter, fetchFilteredWebsites, resetFilters } from '../slices/websitesSlice';
import { getPublishers } from '../../publishers/api';
import { Publisher } from '../../../types/publisher.types';

const WebsiteFilters: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { filters } = useSelector((state: RootState) => state.websites);
  
  const [publishers, setPublishers] = useState<Publisher[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load publishers on component mount
  useEffect(() => {
    const loadPublishers = async () => {
      setIsLoading(true);
      try {
        const publisherData = await getPublishers();
        setPublishers(publisherData);
      } catch (error) {
        console.error('Failed to load publishers:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadPublishers();
  }, []);

  const handlePublisherChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    dispatch(setFilter({ key: 'publisherPK', value }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    dispatch(setFilter({ key: 'searchTerm', value }));
  };

  const handleApplyFilters = () => {
    dispatch(fetchFilteredWebsites(filters));
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
    dispatch(fetchFilteredWebsites({}));
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth disabled={isLoading}>
            <InputLabel id="publisher-select-label">Publisher</InputLabel>
            <Select
              labelId="publisher-select-label"
              id="publisher-select"
              value={filters.publisherPK || ''}
              label="Publisher"
              onChange={handlePublisherChange}
            >
              <MenuItem value="">
                <em>All Publishers</em>
              </MenuItem>
              {publishers.map((publisher) => (
                <MenuItem key={publisher.id} value={`PUBLISHER#${publisher.id}`}>
                  {publisher.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Search Websites"
            variant="outlined"
            value={filters.searchTerm}
            onChange={handleSearchChange}
            placeholder="Search by name, URL, description..."
          />
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyFilters}
              disabled={isLoading}
            >
              Apply Filters
            </Button>
            <Button
              variant="outlined"
              onClick={handleResetFilters}
              disabled={isLoading}
            >
              Reset
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WebsiteFilters;